import {
  PublicClientApplication,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";
import axios from "axios";
import { jwtDecode } from "jwt-decode";

export const msalConfig = {
  auth: {
    clientId: "a6dd00fe-d3e8-4540-927a-e42f5046e5cb",
    authority: "https://login.microsoftonline.com/common",
    redirectUri: "https://app.bldera.com/callback",
    // redirectUri: "http://localhost:4200/", // Comment out for production
  },
  cache: {
    cacheLocation: "localStorage",
    storeAuthStateInCookie: true,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (!containsPii) console.log(`MSAL: ${message}`);
      },
      piiLoggingEnabled: false,
      logLevel: "info",
    },
  },
};

export const loginRequest = {
  scopes: [
    "openid",
    "email",
    "profile",
    "User.Read",
    "Files.ReadWrite.All",
    "Sites.ReadWrite.All",
  ],
  authority: "https://login.microsoftonline.com/common",
};

export const msalInstance = new PublicClientApplication(msalConfig);

let isInitialized = false;

export const initializeMsal = async () => {
  if (!isInitialized) {
    console.log("Initializing MSAL...");
    await msalInstance.initialize();
    isInitialized = true;
    console.log("MSAL initialized successfully");
  }
};

export async function clearAuthCache() {
  localStorage.clear();
  sessionStorage.clear();
  msalInstance.clearCache();
  console.log("MSAL cache and browser storage cleared");
}

export async function acquireTokenSilent(
  account,
  scopes = loginRequest.scopes,
) {
  if (!account) {
    console.error("No account provided for token acquisition");
    throw new Error("No account found. Please sign in again.");
  }
  const tokenRequest = { account, scopes, authority: loginRequest.authority };
  try {
    console.log(
      "Attempting silent token acquisition for tenant:",
      account.tenantId,
    );
    const response = await msalInstance.acquireTokenSilent(tokenRequest);
    const decodedToken = jwtDecode(response.idToken);
    console.log("Token acquired silently for tenant:", decodedToken.tid);
    return response.accessToken;
  } catch (error) {
    console.error("Silent token acquisition failed:", error);
    if (error instanceof InteractionRequiredAuthError) {
      console.log("Interaction required, attempting redirect...");
      await msalInstance.acquireTokenRedirect(tokenRequest);
      throw new Error("Authentication required, redirecting...");
    }
    throw error;
  }
}

export async function handleRedirect() {
  try {
    console.log("Handling redirect, current hash:", window.location.hash);
    const response = await msalInstance.handleRedirectPromise();
    if (response) {
      console.log("MSAL response:", response);
      const decodedToken = jwtDecode(response.idToken);
      console.log("Decoded ID Token:", decodedToken);
      const tenantId = decodedToken.tid; // Extract tenant ID
      localStorage.setItem("tenantId", tenantId); // Store tenant ID
      console.log("Tenant ID stored:", tenantId);

      if (
        decodedToken.iss !==
        "https://login.microsoftonline.com/{tenantid}/v2.0".replace(
          "{tenantid}",
          tenantId,
        )
      ) {
        console.warn(
          "Unexpected issuer in ID token:",
          decodedToken.iss,
          "Expected: https://login.microsoftonline.com/{tenantid}/v2.0",
        );
      }

      msalInstance.setActiveAccount(response.account);
      const backendResponse = await axios.post(
        "https://erp-production-72da01c8e651.herokuapp.com/api/auth/login",
        { idToken: response.idToken, tenantId },
      );
      if (backendResponse.data.success) {
        localStorage.setItem("token", backendResponse.data.token);
        localStorage.setItem("isLoggedIn", "true");
        return {
          account: response.account,
          user: backendResponse.data.user,
          tenantId,
        };
      } else {
        throw new Error(
          "Backend login failed: " + backendResponse.data.message,
        );
      }
    }
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
      const tenantId = accounts[0].tenantId;
      localStorage.setItem("tenantId", tenantId);
      return { account: accounts[0], tenantId };
    }
    return null;
  } catch (error) {
    console.error("Redirect handling failed:", error);
    throw error;
  }
}

export async function getAccessToken() {
  await initializeMsal();
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    console.warn("No accounts found, redirecting to login...");
    await clearAuthCache();
    await msalInstance.loginRedirect(loginRequest);
    return null;
  }
  try {
    const accessToken = await acquireTokenSilent(accounts[0]);
    return accessToken;
  } catch (error) {
    console.error("Failed to acquire token:", error);
    return null;
  }
}

export async function reauthenticateWithBackend() {
  await initializeMsal();
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    console.warn("No accounts found, redirecting to login...");
    await clearAuthCache();
    await msalInstance.loginRedirect(loginRequest);
    return null;
  }
  try {
    const response = await acquireTokenSilent(accounts[0]);
    const decodedToken = jwtDecode(response.idToken);
    const tenantId = decodedToken.tid;
    console.log("Re-auth ID Token for tenant:", tenantId);
    const backendResponse = await axios.post(
      "https://erp-production-72da01c8e651.herokuapp.com/api/auth/login",
      { idToken: response.idToken, tenantId },
    );
    if (backendResponse.data.success) {
      localStorage.setItem("token", backendResponse.data.token);
      localStorage.setItem("tenantId", tenantId);
      return backendResponse.data.token;
    }
    throw new Error("Backend login failed: " + backendResponse.data.message);
  } catch (error) {
    console.error("Backend re-authentication failed:", error);
    return null;
  }
}

export async function logout() {
  await clearAuthCache();
  await msalInstance.logoutRedirect({
    postLogoutRedirectUri: "https://app.bldera.com/login",
  });
}
